import { AsyncPipe, NgIf } from '@angular/common';
import { Component, DestroyRef, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Language } from '@lx/language';
import { provideComponentStore } from '@ngrx/component-store';
import { Observable, distinctUntilChanged, filter, map, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { AyaScheduleService } from '../../services';
import { AyaLocationService } from '../../services/aya-location.service';
import { CourseState, CourseStore, SharedDataStore } from '../../store';
import { CourseDetails } from '@lx/quote-tool/lite/shared/types';
import { AyaDuration, CoursePayload, FixedDate, GroupedSchoolLocation } from '../../types';
import { AyaCourseSelectorFieldComponent } from '../aya-course-selector-field/aya-course-selector-field.component';
import { DatesComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { FixedDateSelectorFieldComponent } from '../fixed-date-selector-field/fixed-date-selector-field.component';
import { LocationSelectorFieldComponent } from '../location-selector-field/location-selector-field.component';
import { SingleSelectionButtonListFieldComponent } from '../single-selection-button-list-field/single-selection-button-list-field.component';
import { SingleSelectionButtonListFieldItem } from '../single-selection-button-list-field/single-selection-button-list-field.type';
import { QTLiteStoreSignalsAdapterService } from '../../services/qt-lite-store-signals-adapter.service';
import { SchoolLocation } from '@lx/quote-tool/lite/shared/types';

@Component({
  standalone: true,
  selector: 'lx-academic-year-abroad',
  imports: [
    AyaCourseSelectorFieldComponent,
    AsyncPipe,
    NgIf,
    DatesComponent,
    LocationSelectorFieldComponent,
    SingleSelectionButtonListFieldComponent,
    FormsModule,
    FixedDateSelectorFieldComponent,
  ],
  templateUrl: './academic-year-abroad.component.html',
  styleUrls: ['./academic-year-abroad.component.scss'],
  providers: [provideComponentStore(CourseStore)],
})
export class AcademicYearAbroadComponent implements OnInit {
  @Output() coursePayload = new EventEmitter<CoursePayload>();
  @Output() validCoursePayload = new EventEmitter<boolean>();

  courseDetails$: Observable<Partial<CourseState>> | undefined;
  languages$: Observable<Language[]> | undefined;
  groupedSchoolLocations$: Observable<GroupedSchoolLocation[]> | undefined;
  durations$: Observable<SingleSelectionButtonListFieldItem<AyaDuration>[]> | undefined;
  ayaStartDates$: Observable<FixedDate[]> | undefined;

  readonly emptySelectionMsg = $localize`Select`;

  private readonly DEFAULT_DURATION_OPTIONS = [
    { value: { weeks: 22, months: 6 }, name: '6' },
    { value: { weeks: 32, months: 9 }, name: '9' },
    { value: { weeks: 42, months: 11 }, name: '11' },
  ];

  private readonly courseStore = inject(CourseStore);
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ayaLocationService = inject(AyaLocationService);
  private readonly ayaScheduleService = inject(AyaScheduleService);
  private readonly qtLiteStoreSignalsAdapterService = inject(QTLiteStoreSignalsAdapterService);

  protected selectedStartDate = this.qtLiteStoreSignalsAdapterService.selectedStartDate;
  protected selectedEndDate = this.qtLiteStoreSignalsAdapterService.selectedEndDate;

  constructor() {
    this.courseDetails$ = this.courseStore.ayaCourseDetails$;
    this.languages$ = this.sharedDataStore.languages$;
  }

  ngOnInit(): void {
    this.courseStore.isAyaValid$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((valid) => this.validCoursePayload.emit(valid));
    this.courseDetails$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((courseDetails) => this.coursePayload.emit(courseDetails));

    this.groupedSchoolLocations$ = this.courseStore.selectedCourseAndLanguage$.pipe(
      map(({ selectedCourse, selectedLanguage }) => ({ courseCode: selectedCourse?.code ?? null, languageCode: selectedLanguage })),
      filter(({ courseCode, languageCode }) => !!courseCode && !!languageCode),
      distinctUntilChanged(),
      switchMap(({ courseCode, languageCode }) => this.ayaLocationService.getAyaLocations(courseCode, languageCode)),
    );

    this.courseStore.selectedSchoolLocation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((location) => {
      if (!location) {
        this.updateValueInStore(undefined, 'selectedAyaDuration');
      }
    });

    this.ayaStartDates$ = this.courseStore.selectedSchoolCodeCourseAndAyaDuration$.pipe(
      filter(
        (d): d is { selectedCourse: CourseDetails; selectedSchoolCode: string; selectedAyaDuration: AyaDuration } =>
          !!d.selectedCourse && !!d.selectedSchoolCode && !!d.selectedAyaDuration,
      ),
      switchMap(({ selectedSchoolCode, selectedCourse, selectedAyaDuration }) =>
        this.ayaScheduleService.getStartDates(selectedCourse?.code, selectedSchoolCode, Number(selectedAyaDuration.months)),
      ),
    );

    this.initializeDurations();
  }

  updateSelectedLanguage(selectedLanguage: string) {
    this.updateValueInStore(selectedLanguage, 'selectedLanguage');
  }

  updateSelectedCourse(selectedCourse: CourseDetails) {
    this.updateValueInStore(selectedCourse, 'selectedCourse');
  }

  updateSelectedSchoolLocation(schoolLocation: SchoolLocation) {
    this.updateValueInStore(schoolLocation, 'selectedSchoolLocation');
  }

  updateSelectedDuration(selectedAyaDuration: AyaDuration) {
    this.updateValueInStore(selectedAyaDuration, 'selectedAyaDuration');
  }

  updateSelectedStartDate(selectedStartDate: FixedDate) {
    this.updateValueInStore(selectedStartDate?.date, 'selectedStartDate');
  }

  compareDurations = (a: AyaDuration, b: AyaDuration) => {
    return a?.months === b?.months;
  };

  private updateValueInStore(value: CourseDetails | AyaDuration | undefined | SchoolLocation | string, key: keyof CoursePayload) {
    this.courseStore.updateState({
      [key]: value,
    });
  }

  private initializeDurations(): void {
    this.durations$ = this.courseStore.selectedSchoolLocationAndCourse$.pipe(
      map(({ selectedCourse, selectedSchoolLocation }) => {
        const courseCode = selectedCourse?.code;
        const schoolCode = selectedSchoolLocation?.schoolCode;
        return { courseCode, schoolCode };
      }),
      switchMap(({ courseCode, schoolCode }) => {
        // We only get durations once both a course and a school are selected.
        // Until then, show placeholder options so the field is not empty.
        if (!courseCode || !schoolCode) {
          return of(this.DEFAULT_DURATION_OPTIONS);
        }

        return this.ayaScheduleService.getDurationOptions(courseCode, schoolCode);
      }),
    );
  }
}
